import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext.js';
import ClinicSettingsService from '../../services/ClinicSettingsService.js';

const ClinicSettings = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.roles?.includes('Administrator');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [companyName, setCompanyName] = useState('Clinic Management');
  const [logoDataUrl, setLogoDataUrl] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await ClinicSettingsService.getSettings();
        const data = res?.data ?? res;
        setCompanyName(data?.companyName ?? data?.CompanyName ?? 'Clinic Management');
        setLogoDataUrl(data?.logoDataUrl ?? data?.LogoDataUrl ?? null);
      } catch (e) {
        setError(e?.message || 'Failed to load clinic settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const onLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      setLogoDataUrl(typeof result === 'string' ? result : null);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = {
        CompanyName: companyName,
        LogoDataUrl: logoDataUrl,
      };

      const res = await ClinicSettingsService.updateSettings(payload);
      const data = res?.data ?? res;
      setCompanyName(data?.companyName ?? data?.CompanyName ?? companyName);
      setLogoDataUrl(data?.logoDataUrl ?? data?.LogoDataUrl ?? logoDataUrl);
      setSuccess('Settings saved');
    } catch (e) {
      setError(e?.message || 'Failed to save clinic settings');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error">Access Denied</Typography>
        <Typography variant="body1">You do not have permission to access this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Clinic Settings</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Paper sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Button variant="outlined" component="label" disabled={saving}>
                  Upload Logo
                  <input type="file" accept="image/*" hidden onChange={onLogoFileChange} />
                </Button>
                <Button
                  variant="text"
                  color="error"
                  onClick={() => setLogoDataUrl(null)}
                  disabled={saving || !logoDataUrl}
                >
                  Remove Logo
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography variant="subtitle2">Preview</Typography>
                {logoDataUrl ? (
                  <Box
                    component="img"
                    src={logoDataUrl}
                    alt="Logo Preview"
                    sx={{ width: 120, height: 120, objectFit: 'contain', border: '1px solid #eee', borderRadius: 1, p: 1 }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">No logo</Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={24} /> : 'Save'}
              </Button>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default ClinicSettings;
